import React, { useState, useEffect, useRef } from 'react';

import { Routes, Route, Navigate } from 'react-router-dom';
import { User as UserIcon, LogOut as LogOutIcon, Bell as BellIcon, X as CloseIcon } from 'lucide-react';
import './App.css';
import Sidebar from './System Design/Sidebar';
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';
import Upload from './pages/Upload/Upload';
import Documents from './pages/Documents/Documents';
import DocumentViewer from './pages/DocumentViewer/DocumentViewer';
import TagsDocumentation from './pages/TagsDocumentation';
import authService from './services/authService';
import Profile from './pages/Profile/Profile';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import UsersManagement from './pages/Admin/UsersManagement';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import notificationService from './services/notificationService';
import { useLanguage } from './contexts/LanguageContext';

// Composant pour protéger les routes nécessitant une authentification
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authStatus = authService.isAuthenticated();
        if (authStatus) {
          // Optionnel: valider le token avec le backend
          try {
            await authService.validateToken();
            setIsAuthenticated(true);
          } catch (error) {
            console.log('Token invalide, redirection vers login');
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification d\'authentification:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Composant pour protéger les routes réservées aux administrateurs
const AdminRoute = ({ children }) => {
  const [allowed, setAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const authStatus = authService.isAuthenticated();
        if (authStatus) {
          try {
            await authService.validateToken();
          } catch (error) {
            // Token invalide, on refusera plus bas
          }
        }
        setAllowed(authStatus && authService.isAdmin());
      } catch (error) {
        setAllowed(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdmin();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return allowed ? children : <Navigate to="/" replace />;
};

// Composant pour protéger les routes accessibles aux administrateurs et aux managers (lecture seule côté UI pour managers)
const AdminOrManagerRoute = ({ children }) => {
  const [allowed, setAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        const authStatus = authService.isAuthenticated();
        if (authStatus) {
          try {
            await authService.validateToken();
          } catch (error) {
            // Token invalide, on refusera plus bas
          }
        }
        setAllowed(authStatus && (authService.isAdmin() || authService.isManager()));
      } catch (error) {
        setAllowed(false);
      } finally {
        setIsLoading(false);
      }
    };

    check();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return allowed ? children : <Navigate to="/" replace />;
};

// Composant pour le layout principal
const MainLayout = () => {
  const { isCollapsed, setIsMobileOpen } = useSidebar();
  const [mobileUserOpen, setMobileUserOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('all');

  const mobileUserRef = useRef(null);

  const { t, language } = useLanguage();

  const getLocaleForLanguage = (lang) => {
    if (lang === 'en') return 'en-US';
    if (lang === 'pt') return 'pt-PT';
    return 'fr-FR';
  };

  const dateLocale = getLocaleForLanguage(language);

  useEffect(() => {
    const isAuthed = authService.isAuthenticated();
    if (!isAuthed) {
      return;
    }

    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let wsUrl;
    if (window.location.port === '3000') {
      // En développement (frontend sur :3000), passer par Nginx sur :8017 pour les WebSockets
      wsUrl = `${scheme}://${window.location.hostname}:8017/ws/notifications/`;
    } else {
      wsUrl = `${scheme}://${window.location.host}/ws/notifications/`;
    }

    // Ajouter le token JWT dans la query string pour authentifier la connexion WS
    try {
      const token = authService.getToken && authService.getToken();
      if (token) {
        const sep = wsUrl.includes('?') ? '&' : '?';
        wsUrl = `${wsUrl}${sep}token=${encodeURIComponent(token)}`;
      }
    } catch (_) {
      // En cas d'erreur de lecture du token, on tente quand même la connexion sans JWT
    }

    const socket = new WebSocket(wsUrl);

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (Array.isArray(data.notifications)) {
          setNotifications((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const merged = [...data.notifications.filter((n) => !existingIds.has(n.id)), ...prev];
            return merged.slice(0, 50);
          });
        } else if (data.id) {
          setNotifications((prev) => {
            if (prev.some((n) => n.id === data.id)) return prev;
            return [data, ...prev].slice(0, 50);
          });
        }
      } catch (e) {
        // Ignorer les erreurs de parsing
      }
    };

    socket.onerror = () => {
      socket.close();
    };

    return () => {
      try {
        socket.close();
      } catch (_) {}
    };
  }, []);

  // Charger les notifications existantes au chargement de l'application
  useEffect(() => {
    const isAuthed = authService.isAuthenticated();
    if (!isAuthed) {
      return;
    }

    const fetchNotifications = async () => {
      try {
        const data = await notificationService.list();
        if (Array.isArray(data)) {
          setNotifications((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const merged = [...data.filter((n) => n && !existingIds.has(n.id)), ...prev];
            return merged.slice(0, 50);
          });
        }
      } catch (e) {
        // On ignore les erreurs de chargement des notifications
      }
    };

    fetchNotifications();
  }, []);

  // Fermer le menu profil mobile lorsqu'on clique en dehors
  useEffect(() => {
    if (!mobileUserOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (mobileUserRef.current && !mobileUserRef.current.contains(event.target)) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileUserOpen]);

  const userName = (authService.getUserName && authService.getUserName()) || 'Utilisateur';
  const initials = (userName || 'U')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleMobileProfile = () => {
    setMobileUserOpen(false);
    window.location.href = '/profile';
  };

  const handleMobileLogout = async () => {
    setMobileUserOpen(false);
    try {
      await authService.logout();
    } finally {
      window.location.href = '/login';
    }
  };

  const toggleNotifications = () => {
    setNotificationsOpen((open) => !open);
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const filteredNotifications = notifications.filter((n) => {
    if (notificationFilter === 'unread') return !n.is_read;
    if (notificationFilter === 'read') return n.is_read;
    return true;
  });

  const notificationCountLabel =
    notifications.length === 1
      ? t('notifications.count.singleLabel')
      : t('notifications.count.pluralLabel');

  const unreadCountLabel =
    unreadCount === 1
      ? t('notifications.count.unreadSingle')
      : t('notifications.count.unreadPlural');

  const handleNotificationClick = async (notif) => {
    if (!notif) return;
    const notifId = notif.id;

    // Marquer comme lue côté état immédiatement
    if (notifId && !notif.is_read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
      try {
        await notificationService.markRead(notifId);
      } catch (_) {
        // Ignorer les erreurs ponctuelles
      }
    }

    // Si la notification référence un livre, ouvrir la page du livre
    const bookId = notif?.data?.book_id;
    if (bookId) {
      window.location.href = `/documents/${bookId}`;
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    // Mettre à jour l'état local en premier
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await notificationService.markAllRead();
    } catch (_) {
      // Ignorer les erreurs ponctuelles
    }
  };

  return (
    <div className="flex">
      <Sidebar
        notificationsCount={unreadCount}
        onNotificationsClick={toggleNotifications}
      />

      <div className={`flex-1 transition-all duration-300 ml-0 ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Barre supérieure mobile avec bouton hamburger + profil */}
        <div className="mobile-navbar md:hidden px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIsMobileOpen(true)}
            className="p-2 rounded-md border border-gray-200 bg-white shadow-sm flex flex-col justify-center gap-1"
            aria-label="Ouvrir le menu"
          >
            <span className="block w-5 h-0.5 bg-gray-800 rounded" />
            <span className="block w-5 h-0.5 bg-gray-800 rounded" />
            <span className="block w-5 h-0.5 bg-gray-800 rounded" />
          </button>

          {/* Profil mobile (avatar + menu) */}
          <div className="relative flex items-center gap-4" ref={mobileUserRef}>
            <button
              type="button"
              className="relative"
              onClick={toggleNotifications}
              aria-label="Ouvrir les notifications"
            >
              <BellIcon className="w-5 h-5 text-gray-700" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[10px] px-1 min-w-[16px] text-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <button
              type="button"
              className="flex items-center gap-2"
              onClick={() => setMobileUserOpen((o) => !o)}
              aria-label="Ouvrir le menu profil"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold">
                {initials}
              </div>
            </button>

            {mobileUserOpen && (
              <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm" style={{ zIndex: 9999, width: '160px', minWidth: '160px', whiteSpace: 'nowrap' }}>
                <button
                  type="button"
                  onClick={handleMobileProfile}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50"
                >
                  <UserIcon className="w-4 h-4" />
                  <span>{t('sidebar.profile')}</span>
                </button>

                <button
                  type="button"
                  onClick={handleMobileLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50"
                >
                  <LogOutIcon className="w-4 h-4" />
                  <span>{t('sidebar.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/upload" element={
            <AdminRoute>
              <Upload />
            </AdminRoute>
          } />
          <Route path="/documents" element={<Documents />} />
          <Route path="/documents/:id" element={<DocumentViewer />} />
          <Route path="/tags-documentation" element={<TagsDocumentation />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin/users" element={
            <AdminOrManagerRoute>
              <UsersManagement />
            </AdminOrManagerRoute>
          } />
        </Routes>
      </div>

      {notificationsOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            style={{ zIndex: 2990 }}
            onClick={() => setNotificationsOpen(false)}
          />
          <div
            className="fixed right-0 top-0 bottom-0 w-full sm:w-96 md:w-[420px] bg-white shadow-2xl z-50 flex flex-col"
            style={{ zIndex: 3000 }}
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">{t('notifications.panelTitle')}</p>
                <p className="text-xs text-gray-500">
                  {notifications.length === 0
                    ? t('notifications.empty')
                    : `${notifications.length} ${notificationCountLabel} (${unreadCount} ${unreadCountLabel})`}
                </p>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setNotificationFilter('all')}
                    className={`px-2 py-1 rounded-full border ${notificationFilter === 'all' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    {t('notifications.filters.all')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotificationFilter('unread')}
                    className={`px-2 py-1 rounded-full border ${notificationFilter === 'unread' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    {t('notifications.filters.unread')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotificationFilter('read')}
                    className={`px-2 py-1 rounded-full border ${notificationFilter === 'read' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    {t('notifications.filters.read')}
                  </button>
                </div>
              </div>

              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllNotificationsRead}
                  className="mr-2 text-xs text-primary hover:text-primary-700"
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setNotificationsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                aria-label="Fermer les notifications"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto divide-y pt-1">
              {filteredNotifications.length === 0 && (
                <div className="p-1 text-sm text-gray-500">
                  {/* TODO: Ajouter un message pour les notifications vides */}
                </div>
              )}
              {filteredNotifications.map((notif, index) => {
                const createdAt = notif.created_at ? new Date(notif.created_at) : null;
                const createdLabel = createdAt
                  ? createdAt.toLocaleString(dateLocale)
                  : '';

                return (
                  <div
                    key={notif.id || index}
                    className={`px-4 py-3 hover:bg-gray-50 flex flex-col gap-1 cursor-pointer ${notif.is_read ? '' : 'bg-blue-50'}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {notif.message || notif.type || t('notifications.labels.default')}
                    </p>
                    {notif.data && notif.data.book_title && (
                      <p className="text-xs text-gray-600">
                        {t('notifications.labels.book')} : {notif.data.book_title}
                      </p>
                    )}

                    {createdLabel && (
                      <p className="text-[11px] text-gray-400">{createdLabel}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <SidebarProvider>
      <div className="bg-gray-50 min-h-screen">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </SidebarProvider>
  );
}

export default App;
 